import { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  ChevronRight, ChevronDown, Plus, X, ExternalLink, FileText, Search,
  GripVertical, Loader2, Upload, Download, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { FileUpload } from '@/components/FileUpload';
import { useAuth } from '@/contexts/AuthContext';


// ─── Constants ───────────────────────────────────────────────────────────
const STATUS_COLUMNS = ['Requested', 'In Progress', 'In Review', 'Published', 'On Hold'];
const CONTENT_TYPES = ['Blog Post', 'Newsletter', 'Social Post', 'Landing Page', 'Page Optimization'];
const KEYWORD_TYPES = ['Primary', 'Secondary', 'Long Tail'];
const DIFFICULTIES = ['Low', 'Medium', 'High'];
const INTENTS = ['Informational', 'Commercial', 'Transactional', 'Navigational'];
const STRATEGY_STATUSES = ['Draft', 'Active', 'Complete'];
const WEEKLY_STATUSES = ['Draft', 'In Progress', 'Complete', 'Skipped'];
const MAX_ATTACHMENTS = 3;

const BADGE = {
  contentType: {
    'Blog Post': 'bg-primary/15 text-primary border-primary/30',
    'Newsletter': 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    'Social Post': 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30',
    'Landing Page': 'bg-sky-500/15 text-sky-300 border-sky-500/30',
    'Page Optimization': 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  },
  type: {
    Primary: 'bg-primary/15 text-primary border-primary/30',
    Secondary: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
    'Long Tail': 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30',
  },
  difficulty: {
    Low: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    Medium: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    High: 'bg-red-500/15 text-red-300 border-red-500/30',
  },
  intent: {
    Informational: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
    Commercial: 'bg-primary/15 text-primary border-primary/30',
    Transactional: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    Navigational: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  },
  weekStatus: {
    Draft: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
    'In Progress': 'bg-primary/15 text-primary border-primary/30',
    Complete: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    Skipped: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
  },
};

function badgeClass(kind, value) {
  return BADGE[kind]?.[value] || 'bg-secondary text-muted-foreground border-border';
}

function formatDate(iso, opts = { month: 'short', day: 'numeric', year: 'numeric' }) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { ...opts, timeZone: 'UTC' });
}

function isInCurrentMonth(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  return !Number.isNaN(d.getTime())
    && d.getUTCMonth() === now.getUTCMonth()
    && d.getUTCFullYear() === now.getUTCFullYear();
}


// ═══════════════════════════════════════════════════════════════════════════
// SECTION 1 - Current Monthly Focus
// ═══════════════════════════════════════════════════════════════════════════
function CurrentMonthlyFocus({ activeStrategy, canEdit, onCreateClick }) {
  if (!activeStrategy) {
    return (
      <Card className="border-white/10 bg-card shadow-none" data-testid="current-focus-empty">
        <CardContent className="p-6 text-center space-y-3">
          <p className="text-sm text-muted-foreground">No Monthly Strategy is currently active.</p>
          {canEdit && (
            <Button
              size="sm"
              onClick={onCreateClick}
              className="bg-primary text-primary-foreground gap-1.5"
              data-testid="current-focus-create-btn"
            >
              <Plus className="h-3.5 w-3.5" /> Create Monthly Strategy
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }
  const rows = [
    { label: 'Theme / Focus', value: activeStrategy.theme },
    { label: 'Target Keyword Clusters', value: activeStrategy.keyword_clusters },
    { label: 'Notes for Sheena', value: activeStrategy.notes_for_sheena },
  ];
  return (
    <Card className="border-primary/30 bg-primary/[0.04] shadow-none" data-testid="current-focus-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-primary" aria-hidden />
          <p className="text-[11px] uppercase tracking-[0.08em] font-medium text-primary">Active Strategy</p>
          {activeStrategy.month && (
            <span className="text-[11px] font-mono tabular-nums text-muted-foreground ml-auto">
              {formatDate(activeStrategy.month, { month: 'long', year: 'numeric' })}
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          {rows.map(r => (
            <div key={r.label} className="space-y-1" data-testid={`focus-row-${r.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <p className="text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground">{r.label}</p>
              <p className="text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed">
                {r.value || <span className="text-muted-foreground/60 italic">Not set</span>}
              </p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}


// All Monthly Strategies list. Shows every strategy regardless of status so
// Draft entries stay visible for the user to promote to Active. Fixes the
// silent "save didn't work" perception where Draft strategies persisted but
// never appeared on this page.
function AllStrategiesList({ strategies, canEdit, onCreateClick, onEditClick }) {
  const sorted = [...strategies].sort((a, b) => (b.month || '').localeCompare(a.month || ''));
  const STATUS_BADGE = {
    'Active': 'bg-primary/15 text-primary border-primary/30',
    'Draft': 'bg-slate-500/15 text-slate-300 border-slate-500/30',
    'Archived': 'bg-muted text-muted-foreground border-border',
  };
  return (
    <Card className="border-white/10 bg-card shadow-none" data-testid="all-strategies-card">
      <CardContent className="p-0">
        <div className="p-4 flex items-center justify-between border-b border-white/10">
          <div>
            <h3 className="text-sm font-medium uppercase tracking-[0.08em] text-muted-foreground">All Monthly Strategies</h3>
            <p className="text-[11px] text-muted-foreground/70 mt-0.5">Every strategy across statuses. Click a row to edit or change status.</p>
          </div>
          {canEdit && (
            <Button size="sm" variant="outline" onClick={onCreateClick} className="gap-1.5" data-testid="new-strategy-btn">
              <Plus className="h-3.5 w-3.5" /> New Strategy
            </Button>
          )}
        </div>
        {sorted.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground text-center">No strategies yet. Click <span className="text-foreground">New Strategy</span> to create one.</p>
        ) : (
          <div className="divide-y divide-white/5">
            {sorted.map(s => (
              <button
                key={s.id}
                type="button"
                onClick={() => canEdit && onEditClick(s)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${canEdit ? 'hover:bg-secondary/40 cursor-pointer' : 'cursor-default'}`}
                data-testid={`strategy-row-${s.id}`}
              >
                <span className="font-mono tabular-nums text-xs text-foreground/90 w-24">
                  {s.month ? formatDate(s.month, { month: 'short', year: 'numeric' }) : '-'}
                </span>
                <Badge variant="outline" className={`text-[10px] uppercase tracking-[0.05em] ${STATUS_BADGE[s.status] || 'bg-secondary'}`}>
                  {s.status || 'Draft'}
                </Badge>
                <span className="text-sm text-foreground/85 truncate flex-1">
                  {s.theme || <span className="italic text-muted-foreground/60">No theme set</span>}
                </span>
                {(s.linked_keyword_group_ids || []).length > 0 && (
                  <span className="text-[10px] uppercase tracking-[0.05em] text-muted-foreground">
                    {(s.linked_keyword_group_ids || []).length} linked group(s)
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// SECTION 2 - Keyword Groups
// ═══════════════════════════════════════════════════════════════════════════
function KeywordGroupRow({ week, keywords, expanded, onToggle }) {
  return (
    <>
      <TableRow
        className="cursor-pointer hover:bg-secondary/40 border-white/10"
        onClick={onToggle}
        data-testid={`week-row-${week.id}`}
      >
        <TableCell className="w-8">
          {expanded
            ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
            : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        </TableCell>
        <TableCell className="font-medium text-foreground">Week {week.week_number || '-'}</TableCell>
        <TableCell className="font-mono tabular-nums text-sm text-muted-foreground">{formatDate(week.date_range)}</TableCell>
        <TableCell>
          <Badge variant="outline" className={`text-[10px] uppercase tracking-[0.05em] ${badgeClass('weekStatus', week.status)}`}>
            {week.status || '-'}
          </Badge>
        </TableCell>
        <TableCell className="text-sm font-mono tabular-nums text-muted-foreground" data-testid={`week-kw-count-${week.id}`}>
          {keywords.length} keyword{keywords.length !== 1 ? 's' : ''}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow className="border-white/10 hover:bg-transparent">
          <TableCell colSpan={5} className="p-0">
            <div className="bg-background/40 border-l-2 border-l-primary/40 px-4 py-3" data-testid={`week-keywords-${week.id}`}>
              {keywords.length === 0 ? (
                <p className="text-xs text-muted-foreground italic py-2">No keywords in this week yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-[10px] uppercase tracking-[0.05em] text-muted-foreground">Keyword</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.05em] text-muted-foreground">Type</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.05em] text-muted-foreground text-right">Volume</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.05em] text-muted-foreground">Difficulty</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.05em] text-muted-foreground">Intent</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {keywords.map(kw => (
                      <TableRow key={kw.id} className="border-white/10">
                        <TableCell className="text-sm">{kw.keyword}</TableCell>
                        <TableCell><Badge variant="outline" className={`text-[10px] uppercase ${badgeClass('type', kw.type)}`}>{kw.type}</Badge></TableCell>
                        <TableCell className="text-sm font-mono tabular-nums text-right">{(kw.search_volume || 0).toLocaleString()}</TableCell>
                        <TableCell><Badge variant="outline" className={`text-[10px] uppercase ${badgeClass('difficulty', kw.difficulty)}`}>{kw.difficulty}</Badge></TableCell>
                        <TableCell><Badge variant="outline" className={`text-[10px] uppercase ${badgeClass('intent', kw.intent)}`}>{kw.intent}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}


function KeywordGroupsSection({ weeks, keywordsByWeek, canEdit, onAddClick }) {
  const [expandedId, setExpandedId] = useState(null);
  return (
    <Card className="border-white/10 bg-card shadow-none">
      <CardContent className="p-0">
        <div className="p-4 flex items-center justify-between border-b border-white/10">
          <h3 className="text-sm font-medium uppercase tracking-[0.08em] text-muted-foreground">Keyword Groups</h3>
          {canEdit && (
            <Button size="sm" variant="outline" onClick={onAddClick} className="gap-1.5" data-testid="add-weekly-keywords-btn">
              <Plus className="h-3.5 w-3.5" /> Add Week
            </Button>
          )}
        </div>
        {weeks.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground text-center">No keyword groups yet. Add a week to get started.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="w-8" />
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Week</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Date Range</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Status</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Keywords</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {weeks.map(w => (
                <KeywordGroupRow
                  key={w.id}
                  week={w}
                  keywords={keywordsByWeek[w.id] || []}
                  expanded={expandedId === w.id}
                  onToggle={() => setExpandedId(expandedId === w.id ? null : w.id)}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// SECTION 3 - Content Requests (Draggable Kanban)
// ═══════════════════════════════════════════════════════════════════════════
function ContentKanbanCard({ card, onClick, onDragStart, canEdit }) {
  return (
    <div
      role="button"
      tabIndex={0}
      draggable={canEdit}
      onDragStart={canEdit ? (e) => onDragStart(e, card.id) : undefined}
      onClick={() => onClick(card)}
      onKeyDown={(e) => (e.key === 'Enter' ? onClick(card) : null)}
      className={`rounded-md border border-white/10 bg-card p-3 space-y-2 hover:border-primary/40 transition-colors ${canEdit ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}`}
      data-testid={`kanban-card-${card.id}`}
    >
      <div className="flex items-start gap-2">
        {canEdit && <GripVertical className="h-3.5 w-3.5 text-muted-foreground/50 mt-0.5 shrink-0" aria-hidden />}
        <p className="text-sm font-medium leading-tight text-foreground line-clamp-2 flex-1">
          {card.content_title || 'Untitled'}
        </p>
      </div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Badge variant="outline" className={`text-[10px] uppercase ${badgeClass('contentType', card.content_type)}`}>
          {card.content_type || 'Blog Post'}
        </Badge>
        {card.due_date && (
          <span className="text-[11px] text-muted-foreground font-mono tabular-nums">
            {formatDate(card.due_date, { month: 'short', day: 'numeric' })}
          </span>
        )}
      </div>
      {card.assigned_to && (
        <p className="text-[11px] text-muted-foreground">
          <span className="text-muted-foreground/70">Assigned:</span>{' '}
          <span className="text-foreground/90">{card.assigned_to}</span>
        </p>
      )}
      {(card.attachments || []).length > 0 && (
        <div className="flex items-center gap-1 text-[10px] text-muted-foreground/80">
          <FileText className="h-3 w-3" />
          {card.attachments.length} attachment{card.attachments.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}


function ContentKanban({ cards, canEdit, onCardClick, onCardStatusChange }) {
  const [dragOverCol, setDragOverCol] = useState(null);

  const grouped = useMemo(() => {
    const g = Object.fromEntries(STATUS_COLUMNS.map(s => [s, []]));
    for (const c of cards) {
      if (g[c.status]) g[c.status].push(c);
    }
    for (const k of Object.keys(g)) {
      g[k].sort((a, b) => (a.due_date || '9999').localeCompare(b.due_date || '9999'));
    }
    return g;
  }, [cards]);

  const handleDragStart = (e, cardId) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', cardId);
  };
  const handleDrop = (e, targetStatus) => {
    e.preventDefault();
    setDragOverCol(null);
    const id = e.dataTransfer.getData('text/plain');
    if (!id) return;
    const card = cards.find(c => c.id === id);
    if (card && card.status !== targetStatus) {
      onCardStatusChange(id, targetStatus);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="content-kanban-full">
      {STATUS_COLUMNS.map(col => (
        <div
          key={col}
          className={`rounded-md border ${dragOverCol === col ? 'border-primary/60 bg-primary/5' : 'border-white/10 bg-secondary/20'} flex flex-col transition-colors`}
          onDragOver={canEdit ? (e) => { e.preventDefault(); setDragOverCol(col); } : undefined}
          onDragLeave={canEdit ? () => setDragOverCol(null) : undefined}
          onDrop={canEdit ? (e) => handleDrop(e, col) : undefined}
          data-testid={`kanban-full-column-${col.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <div className="px-3 pt-3 pb-2 flex items-center justify-between border-b border-white/10">
            <p className="text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground">{col}</p>
            <span className="text-[11px] font-mono tabular-nums text-muted-foreground/80">{grouped[col].length}</span>
          </div>
          <div className="p-2.5 space-y-2 min-h-[120px] max-h-[560px] overflow-y-auto">
            {grouped[col].length === 0 && (
              <p className="text-[11px] text-muted-foreground/60 italic px-1 py-2">Drop cards here.</p>
            )}
            {grouped[col].map(card => (
              <ContentKanbanCard
                key={card.id}
                card={card}
                canEdit={canEdit}
                onClick={onCardClick}
                onDragStart={handleDragStart}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// SECTION 4 - Keyword Library
// ═══════════════════════════════════════════════════════════════════════════
function KeywordLibrary({ keywords, sources, weeks, canEdit, onAddClick, onImportClick }) {
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [diffFilter, setDiffFilter] = useState('all');
  const [intentFilter, setIntentFilter] = useState('all');

  const sourceById = useMemo(() => Object.fromEntries((sources || []).map(s => [s.id, s])), [sources]);
  const weekById = useMemo(() => Object.fromEntries((weeks || []).map(w => [w.id, w])), [weeks]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return keywords.filter(kw => {
      if (ql && !kw.keyword.toLowerCase().includes(ql)) return false;
      if (typeFilter !== 'all' && kw.type !== typeFilter) return false;
      if (diffFilter !== 'all' && kw.difficulty !== diffFilter) return false;
      if (intentFilter !== 'all' && kw.intent !== intentFilter) return false;
      return true;
    });
  }, [keywords, q, typeFilter, diffFilter, intentFilter]);

  return (
    <Card className="border-white/10 bg-card shadow-none">
      <CardContent className="p-0">
        <div className="p-4 space-y-3 border-b border-white/10">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className="text-sm font-medium uppercase tracking-[0.08em] text-muted-foreground">Keyword Library</h3>
            {canEdit && (
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={onImportClick} className="gap-1.5" data-testid="bulk-import-btn">
                  <Upload className="h-3.5 w-3.5" /> Bulk Import
                </Button>
                <Button size="sm" variant="outline" onClick={onAddClick} className="gap-1.5" data-testid="add-keyword-btn">
                  <Plus className="h-3.5 w-3.5" /> Add Keyword
                </Button>
              </div>
            )}
          </div>
          <div className="flex flex-col md:flex-row gap-2 md:items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search keywords..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9 bg-background border-white/10 h-9 text-sm"
                data-testid="keyword-library-search"
              />
            </div>
            <FilterSelect testId="keyword-library-type-filter" placeholder="All Types" value={typeFilter} onChange={setTypeFilter} options={KEYWORD_TYPES} />
            <FilterSelect testId="keyword-library-difficulty-filter" placeholder="All Difficulties" value={diffFilter} onChange={setDiffFilter} options={DIFFICULTIES} />
            <FilterSelect testId="keyword-library-intent-filter" placeholder="All Intents" value={intentFilter} onChange={setIntentFilter} options={INTENTS} />
          </div>
        </div>
        {filtered.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground text-center">
            {keywords.length === 0 ? 'No keywords yet.' : 'No keywords match your filters.'}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Keyword</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Type</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground text-right">Volume</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Difficulty</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Intent</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Source</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">Week</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(kw => (
                <TableRow key={kw.id} className="border-white/10" data-testid={`library-row-${kw.id}`}>
                  <TableCell className="text-sm">{kw.keyword}</TableCell>
                  <TableCell><Badge variant="outline" className={`text-[10px] uppercase ${badgeClass('type', kw.type)}`}>{kw.type}</Badge></TableCell>
                  <TableCell className="text-sm font-mono tabular-nums text-right">{(kw.search_volume || 0).toLocaleString()}</TableCell>
                  <TableCell><Badge variant="outline" className={`text-[10px] uppercase ${badgeClass('difficulty', kw.difficulty)}`}>{kw.difficulty}</Badge></TableCell>
                  <TableCell><Badge variant="outline" className={`text-[10px] uppercase ${badgeClass('intent', kw.intent)}`}>{kw.intent}</Badge></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{sourceById[kw.research_source_id]?.source_name || <span className="italic text-muted-foreground/60">-</span>}</TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono tabular-nums">{weekById[kw.week_id] ? `Week ${weekById[kw.week_id].week_number}` : '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}


function FilterSelect({ value, onChange, options, placeholder, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full md:w-[160px] h-9 bg-background border-white/10 text-sm" data-testid={testId}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{placeholder}</SelectItem>
        {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// MODALS - Monthly Strategy / Weekly Keywords / Keyword / Content Detail
// ═══════════════════════════════════════════════════════════════════════════
function MonthlyStrategyModal({ open, onClose, onSaved, editing, weeklyKeywords, strategies }) {
  const isEdit = !!editing?.id;
  const [form, setForm] = useState({
    month: '', theme: '', keyword_clusters: '', priority_pages: '',
    goals: '', status: 'Draft', notes_for_sheena: '',
    linked_keyword_group_ids: [],
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setForm({
        month: editing.month ? editing.month.slice(0, 10) : '',
        theme: editing.theme || '',
        keyword_clusters: editing.keyword_clusters || '',
        priority_pages: editing.priority_pages || '',
        goals: editing.goals || '',
        status: editing.status || 'Draft',
        notes_for_sheena: editing.notes_for_sheena || '',
        linked_keyword_group_ids: editing.linked_keyword_group_ids || [],
      });
    } else {
      const now = new Date();
      const first = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString().slice(0, 10);
      setForm({
        month: first, theme: '', keyword_clusters: '', priority_pages: '',
        goals: '', status: 'Draft', notes_for_sheena: '',
        linked_keyword_group_ids: [],
      });
    }
  }, [open, editing, isEdit]);

  const strategyLabel = (sid) => {
    const s = strategies?.find(x => x.id === sid);
    if (!s || !s.month) return '';
    const d = new Date(s.month);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
  };

  const toggleGroup = (id) => {
    setForm(f => {
      const cur = f.linked_keyword_group_ids || [];
      return {
        ...f,
        linked_keyword_group_ids: cur.includes(id)
          ? cur.filter(x => x !== id)
          : [...cur, id],
      };
    });
  };

  const submit = async () => {
    if (!form.month) return toast.error('Month is required');
    setSaving(true);
    try {
      const r = isEdit
        ? await api.put(`/monthly-strategies/${editing.id}`, form)
        : await api.post('/monthly-strategies', form);
      toast.success(isEdit ? 'Monthly strategy updated' : 'Monthly strategy created');
      onSaved(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const remove = async () => {
    if (!isEdit) return;
    if (!window.confirm('Delete this monthly strategy? Linked keyword groups must be moved first.')) return;
    setDeleting(true);
    try {
      await api.delete(`/monthly-strategies/${editing.id}`);
      toast.success('Strategy deleted');
      onSaved(null);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
    setDeleting(false);
  };

  const groupOptions = (weeklyKeywords || [])
    .slice()
    .sort((a, b) => (b.date_range || '').localeCompare(a.date_range || ''));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="monthly-strategy-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Monthly Strategy' : 'New Monthly Strategy'}</DialogTitle>
          <DialogDescription>Set the theme, goals and notes that drive digital marketing work for a calendar month.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FormField label="Month" testId="ms-month">
            <Input type="date" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} className="bg-background border-white/10" data-testid="ms-month-input" />
          </FormField>
          <FormField label="Status" testId="ms-status">
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="bg-background border-white/10" data-testid="ms-status-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>{STRATEGY_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </FormField>
          <FormField label="Theme / Focus" testId="ms-theme" className="md:col-span-2">
            <Input value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })} className="bg-background border-white/10" data-testid="ms-theme-input" />
          </FormField>
          <FormField label="Target Keyword Clusters" testId="ms-clusters" className="md:col-span-2">
            <Textarea rows={2} value={form.keyword_clusters} onChange={(e) => setForm({ ...form, keyword_clusters: e.target.value })} className="bg-background border-white/10" data-testid="ms-clusters-input" />
          </FormField>
          <FormField label="Priority Pages" testId="ms-pages" className="md:col-span-2">
            <Textarea rows={2} value={form.priority_pages} onChange={(e) => setForm({ ...form, priority_pages: e.target.value })} className="bg-background border-white/10" data-testid="ms-pages-input" />
          </FormField>
          <FormField label="Goals" testId="ms-goals" className="md:col-span-2">
            <Textarea rows={2} value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} className="bg-background border-white/10" data-testid="ms-goals-input" />
          </FormField>
          <FormField label="Notes for Sheena" testId="ms-notes-sheena" className="md:col-span-2">
            <Textarea rows={3} value={form.notes_for_sheena} onChange={(e) => setForm({ ...form, notes_for_sheena: e.target.value })} className="bg-background border-white/10" data-testid="ms-notes-sheena-input" />
          </FormField>
          <FormField
            label={`Link Existing Keyword Groups (${(form.linked_keyword_group_ids || []).length} selected)`}
            testId="ms-linked-groups"
            className="md:col-span-2"
          >
            {groupOptions.length === 0 ? (
              <p className="text-xs text-muted-foreground italic px-1 py-2">
                No existing keyword groups yet. You can add groups after creating this strategy.
              </p>
            ) : (
              <div
                className="max-h-40 overflow-y-auto rounded-md border border-white/10 bg-background/40 divide-y divide-white/5"
                data-testid="ms-linked-groups-list"
              >
                {groupOptions.map(wk => {
                  const selected = (form.linked_keyword_group_ids || []).includes(wk.id);
                  return (
                    <label
                      key={wk.id}
                      className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-secondary/40 text-sm"
                      data-testid={`ms-linked-group-${wk.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleGroup(wk.id)}
                        className="accent-primary"
                      />
                      <span className="text-foreground">Week {wk.week_number || '-'}</span>
                      <span className="text-muted-foreground font-mono tabular-nums text-xs">{wk.date_range || ''}</span>
                      {strategyLabel(wk.monthly_strategy_id) && (
                        <span className="ml-auto text-[10px] uppercase tracking-[0.05em] text-muted-foreground/70">
                          Originally: {strategyLabel(wk.monthly_strategy_id)}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
          </FormField>
        </div>
        <DialogFooter className="sm:justify-between">
          <div>
            {isEdit && (
              <Button variant="ghost" onClick={remove} disabled={saving || deleting} className="text-destructive hover:text-destructive" data-testid="ms-delete-btn">
                {deleting && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
                Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving || deleting}>Cancel</Button>
            <Button onClick={submit} disabled={saving || deleting} data-testid="ms-save-btn" className="bg-primary text-primary-foreground gap-1.5">
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function WeeklyKeywordsModal({ open, onClose, onSaved, strategies }) {
  const [form, setForm] = useState({
    week_number: '', date_range: '', status: 'Draft', notes: '', monthly_strategy_id: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const now = new Date();
      const today = now.toISOString().slice(0, 10);
      const defaultStrat = strategies.find(s => s.status === 'Active') || strategies[0];
      setForm({
        week_number: '', date_range: today, status: 'Draft', notes: '',
        monthly_strategy_id: defaultStrat?.id || '',
      });
    }
  }, [open, strategies]);

  const submit = async () => {
    if (!form.monthly_strategy_id) return toast.error('Monthly Strategy is required');
    if (!form.week_number) return toast.error('Week Number is required');
    setSaving(true);
    try {
      const r = await api.post('/weekly-keywords', {
        ...form,
        week_number: parseInt(form.week_number, 10) || 0,
      });
      toast.success('Week created');
      onSaved(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="weekly-keywords-modal">
        <DialogHeader>
          <DialogTitle>New Keyword Group (Week)</DialogTitle>
          <DialogDescription>Groups keywords by a specific week within a monthly strategy.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FormField label="Week Number" testId="wk-num">
            <Input type="number" min="1" value={form.week_number} onChange={(e) => setForm({ ...form, week_number: e.target.value })} className="bg-background border-white/10" data-testid="wk-num-input" />
          </FormField>
          <FormField label="Date Range Start" testId="wk-date">
            <Input type="date" value={form.date_range} onChange={(e) => setForm({ ...form, date_range: e.target.value })} className="bg-background border-white/10" data-testid="wk-date-input" />
          </FormField>
          <FormField label="Status" testId="wk-status">
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="bg-background border-white/10" data-testid="wk-status-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>{WEEKLY_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </FormField>
          <FormField label="Monthly Strategy" testId="wk-strategy">
            <Select value={form.monthly_strategy_id} onValueChange={(v) => setForm({ ...form, monthly_strategy_id: v })}>
              <SelectTrigger className="bg-background border-white/10" data-testid="wk-strategy-trigger"><SelectValue placeholder="Pick a strategy" /></SelectTrigger>
              <SelectContent>
                {strategies.length === 0 && <div className="p-2 text-xs text-muted-foreground">No strategies yet</div>}
                {strategies.map(s => (
                  <SelectItem key={s.id} value={s.id}>
                    {formatDate(s.month, { month: 'short', year: 'numeric' })} - {s.status}{s.theme ? ` - ${s.theme.slice(0, 40)}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Notes" testId="wk-notes" className="md:col-span-2">
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-background border-white/10" data-testid="wk-notes-input" />
          </FormField>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="wk-save-btn" className="bg-primary text-primary-foreground gap-1.5">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function KeywordModal({ open, onClose, onSaved, weeks, sources }) {
  const [form, setForm] = useState({
    keyword: '', type: 'Primary', search_volume: '', difficulty: 'Medium',
    intent: 'Informational', notes: '', week_id: '', research_source_id: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        keyword: '', type: 'Primary', search_volume: '', difficulty: 'Medium',
        intent: 'Informational', notes: '',
        week_id: weeks[0]?.id || '', research_source_id: '',
      });
    }
  }, [open, weeks]);

  const submit = async () => {
    if (!form.keyword.trim()) return toast.error('Keyword is required');
    if (!form.week_id) return toast.error('Week is required');
    setSaving(true);
    try {
      const r = await api.post('/keywords', {
        ...form,
        search_volume: parseInt(form.search_volume, 10) || 0,
        research_source_id: form.research_source_id || undefined,
      });
      toast.success('Keyword added');
      onSaved(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="keyword-modal">
        <DialogHeader>
          <DialogTitle>New Keyword</DialogTitle>
          <DialogDescription>Add a target keyword to the library and link it to a week + optional research source.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FormField label="Keyword" testId="kw-keyword" className="md:col-span-2">
            <Input value={form.keyword} onChange={(e) => setForm({ ...form, keyword: e.target.value })} className="bg-background border-white/10" data-testid="kw-keyword-input" placeholder="e.g. structural engineer nyc" />
          </FormField>
          <FormField label="Type" testId="kw-type">
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger className="bg-background border-white/10" data-testid="kw-type-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>{KEYWORD_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </FormField>
          <FormField label="Search Volume" testId="kw-volume">
            <Input type="number" min="0" value={form.search_volume} onChange={(e) => setForm({ ...form, search_volume: e.target.value })} className="bg-background border-white/10" data-testid="kw-volume-input" />
          </FormField>
          <FormField label="Difficulty" testId="kw-difficulty">
            <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
              <SelectTrigger className="bg-background border-white/10" data-testid="kw-difficulty-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>{DIFFICULTIES.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </FormField>
          <FormField label="Intent" testId="kw-intent">
            <Select value={form.intent} onValueChange={(v) => setForm({ ...form, intent: v })}>
              <SelectTrigger className="bg-background border-white/10" data-testid="kw-intent-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>{INTENTS.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
            </Select>
          </FormField>
          <FormField label="Week" testId="kw-week">
            <Select value={form.week_id} onValueChange={(v) => setForm({ ...form, week_id: v })}>
              <SelectTrigger className="bg-background border-white/10" data-testid="kw-week-trigger"><SelectValue placeholder="Pick a week" /></SelectTrigger>
              <SelectContent>
                {weeks.length === 0 && <div className="p-2 text-xs text-muted-foreground">No weeks yet</div>}
                {weeks.map(w => (
                  <SelectItem key={w.id} value={w.id}>
                    Week {w.week_number} - {formatDate(w.date_range)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Research Source" testId="kw-source">
            <Select value={form.research_source_id || 'none'} onValueChange={(v) => setForm({ ...form, research_source_id: v === 'none' ? '' : v })}>
              <SelectTrigger className="bg-background border-white/10" data-testid="kw-source-trigger"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">(none)</SelectItem>
                {sources.map(s => <SelectItem key={s.id} value={s.id}>{s.source_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </FormField>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="kw-save-btn" className="bg-primary text-primary-foreground gap-1.5">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// Bulk Keyword Import Modal
// ═══════════════════════════════════════════════════════════════════════════
const CSV_TEMPLATE_HEADERS = ['keyword', 'type', 'search_volume', 'difficulty', 'intent', 'notes'];
const CSV_TEMPLATE_SAMPLE = 'keyword,type,search_volume,difficulty,intent,notes\n'
  + 'structural engineer NYC,Primary,1200,Medium,Commercial,top-priority page\n'
  + 'NYC engineer consultant,Secondary,800,Low,Informational,\n'
  + 'best structural firm brooklyn,Long Tail,150,Low,Commercial,long-tail cluster\n';

/**
 * RFC-4180-ish CSV parser: handles quoted fields, escaped quotes ("") inside
 * quotes, CRLF, and skips fully-blank lines. Deliberately kept dependency-free.
 */
function parseCsv(text) {
  const rows = [];
  let cur = [];
  let field = '';
  let inQuotes = false;
  let i = 0;
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQuotes = false; i += 1; continue;
      }
      field += c; i += 1; continue;
    }
    if (c === '"') { inQuotes = true; i += 1; continue; }
    if (c === ',') { cur.push(field); field = ''; i += 1; continue; }
    if (c === '\r') { i += 1; continue; }
    if (c === '\n') {
      cur.push(field); rows.push(cur); cur = []; field = ''; i += 1; continue;
    }
    field += c; i += 1;
  }
  if (field.length > 0 || cur.length > 0) { cur.push(field); rows.push(cur); }
  return rows.filter(r => r.some(cell => cell.trim().length > 0));
}


function parseCsvToItems(text) {
  const rows = parseCsv(text);
  if (rows.length === 0) return { headers: [], items: [], parseError: 'CSV is empty.' };
  const headers = rows[0].map(h => h.trim().toLowerCase());
  if (!headers.includes('keyword')) {
    return { headers, items: [], parseError: 'CSV must include a "keyword" column in the header row.' };
  }
  const dataRows = rows.slice(1);
  const items = dataRows.map((row) => {
    const record = {};
    headers.forEach((h, idx) => { record[h] = (row[idx] ?? '').trim(); });
    return {
      keyword: record.keyword || '',
      type: record.type || 'Primary',
      search_volume: record.search_volume || 0,
      difficulty: record.difficulty || 'Medium',
      intent: record.intent || 'Informational',
      notes: record.notes || '',
    };
  }).filter(r => r.keyword.length > 0);
  return { headers, items, parseError: null };
}


function BulkKeywordImportModal({ open, onClose, onSaved, weeks, sources, existingKeywords }) {
  const [weekId, setWeekId] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [csvText, setCsvText] = useState('');
  const [parseError, setParseError] = useState(null);
  const [items, setItems] = useState([]);
  const [onDuplicate, setOnDuplicate] = useState('skip');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);
  const prevOpenRef = useRef(false);

  useEffect(() => {
    // Only reset when the modal transitions from closed -> open. Without
    // this guard, a `weeks` prop refresh triggered by onSaved() would wipe
    // the freshly-set result state before the summary view can render.
    const opening = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (opening) {
      setWeekId(weeks[0]?.id || '');
      setSourceId('');
      setCsvText('');
      setItems([]);
      setParseError(null);
      setOnDuplicate('skip');
      setResult(null);
    }
  }, [open, weeks]);

  // Existing keyword names (lowercased, trimmed) for dupe detection.
  const existingSet = useMemo(() => {
    const s = new Set();
    for (const k of existingKeywords || []) {
      if (k.keyword) s.add(k.keyword.trim().toLowerCase());
    }
    return s;
  }, [existingKeywords]);

  const dupeCount = items.filter(it => existingSet.has(it.keyword.trim().toLowerCase())).length;
  const newCount = items.length - dupeCount;

  const handleParse = (text) => {
    setCsvText(text);
    if (!text.trim()) {
      setItems([]); setParseError(null); return;
    }
    const { items: parsed, parseError: err } = parseCsvToItems(text);
    setItems(parsed);
    setParseError(err);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('CSV too large. Maximum 2 MB.');
      e.target.value = '';
      return;
    }
    const text = await file.text();
    handleParse(text);
    e.target.value = '';
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE_SAMPLE], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'keywords-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const submit = async () => {
    if (!weekId) return toast.error('Pick a target week first');
    if (items.length === 0) return toast.error('Nothing to import');
    setImporting(true);
    try {
      const r = await api.post('/keywords/bulk-import', {
        week_id: weekId,
        research_source_id: sourceId || undefined,
        on_duplicate: onDuplicate,
        items,
      });
      setResult(r.data);
      const s = r.data.summary;
      const parts = [];
      if (s.created) parts.push(`${s.created} created`);
      if (s.merged) parts.push(`${s.merged} merged`);
      if (s.skipped) parts.push(`${s.skipped} skipped`);
      if (s.errors) parts.push(`${s.errors} errors`);
      toast.success(`Import done - ${parts.join(', ') || 'no changes'}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    }
    setImporting(false);
  };

  const closeAndReset = () => {
    setResult(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && closeAndReset()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="bulk-import-modal">
        <DialogHeader>
          <DialogTitle>Bulk Import Keywords</DialogTitle>
          <DialogDescription>
            Paste or upload a CSV with keyword rows. Duplicates by keyword text (case-insensitive) can be skipped or merged into the existing row.
          </DialogDescription>
        </DialogHeader>

        {/* Result view - shown after a successful import */}
        {result ? (
          <div className="space-y-4" data-testid="bulk-import-result">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <ResultTile label="Created" value={result.summary.created} tone="good" testId="bulk-result-created" />
              <ResultTile label="Merged" value={result.summary.merged} tone="good" testId="bulk-result-merged" />
              <ResultTile label="Skipped" value={result.summary.skipped} tone="muted" testId="bulk-result-skipped" />
              <ResultTile label="Errors" value={result.summary.errors} tone={result.summary.errors > 0 ? 'bad' : 'muted'} testId="bulk-result-errors" />
            </div>
            {result.errors.length > 0 && (
              <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3 space-y-1.5 max-h-40 overflow-y-auto" data-testid="bulk-result-error-list">
                <p className="text-[11px] uppercase tracking-[0.08em] font-medium text-red-400">Errors</p>
                {result.errors.map((e, idx) => (
                  <div key={idx} className="text-[11px] text-foreground/90 font-mono">
                    <span className="text-muted-foreground">row {e.row}:</span>{' '}
                    <span className="text-red-300">{e.keyword || '(blank)'}</span>{' '}
                    <span className="text-muted-foreground">- {e.error}</span>
                  </div>
                ))}
              </div>
            )}
            <DialogFooter>
              <Button onClick={closeAndReset} className="bg-primary text-primary-foreground" data-testid="bulk-result-close-btn">Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Target selectors */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormField label="Target Week (required)" testId="bulk-week">
                <Select value={weekId} onValueChange={setWeekId}>
                  <SelectTrigger className="bg-background border-white/10" data-testid="bulk-week-trigger"><SelectValue placeholder="Pick a week" /></SelectTrigger>
                  <SelectContent>
                    {weeks.length === 0 && <div className="p-2 text-xs text-muted-foreground">No weeks yet - create one first.</div>}
                    {weeks.map(w => (
                      <SelectItem key={w.id} value={w.id}>Week {w.week_number} - {formatDate(w.date_range)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Research Source (optional)" testId="bulk-source">
                <Select value={sourceId || 'none'} onValueChange={(v) => setSourceId(v === 'none' ? '' : v)}>
                  <SelectTrigger className="bg-background border-white/10" data-testid="bulk-source-trigger"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">(none)</SelectItem>
                    {sources.map(s => <SelectItem key={s.id} value={s.id}>{s.source_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FormField>
            </div>

            {/* CSV input */}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Label className="text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground">
                  CSV Data
                </Label>
                <div className="flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleFile}
                    className="hidden"
                    data-testid="bulk-csv-file"
                  />
                  <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} className="gap-1.5" data-testid="bulk-upload-btn">
                    <Upload className="h-3 w-3" /> Upload .csv
                  </Button>
                  <Button size="sm" variant="ghost" onClick={downloadTemplate} className="gap-1.5 text-muted-foreground" data-testid="bulk-download-template-btn">
                    <Download className="h-3 w-3" /> Template
                  </Button>
                </div>
              </div>
              <Textarea
                rows={6}
                value={csvText}
                onChange={(e) => handleParse(e.target.value)}
                className="bg-background border-white/10 font-mono text-xs"
                placeholder={CSV_TEMPLATE_SAMPLE}
                data-testid="bulk-csv-textarea"
              />
              <p className="text-[10px] text-muted-foreground/70">
                Header row required. Columns: {CSV_TEMPLATE_HEADERS.join(', ')}. Only `keyword` is required per row; missing fields fall back to Primary / 0 / Medium / Informational.
              </p>
            </div>

            {/* Parse errors */}
            {parseError && (
              <div className="rounded-md border border-red-500/30 bg-red-500/5 px-3 py-2 flex items-start gap-2" data-testid="bulk-parse-error">
                <AlertCircle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" aria-hidden />
                <p className="text-xs text-red-300">{parseError}</p>
              </div>
            )}

            {/* Preview */}
            {items.length > 0 && (
              <div className="space-y-2" data-testid="bulk-preview">
                <div className="flex items-center justify-between text-xs">
                  <p className="text-muted-foreground">
                    Preview: <span className="text-foreground font-mono tabular-nums">{items.length}</span> row{items.length !== 1 ? 's' : ''} -
                    {' '}<span className="text-emerald-400 font-mono tabular-nums">{newCount}</span> new,
                    {' '}<span className="text-amber-400 font-mono tabular-nums">{dupeCount}</span> duplicate{dupeCount !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="max-h-56 overflow-y-auto rounded-md border border-white/10 bg-background/40">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-secondary/40 border-b border-white/10">
                      <tr>
                        <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Keyword</th>
                        <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Type</th>
                        <th className="text-right px-3 py-2 text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Volume</th>
                        <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Difficulty</th>
                        <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Intent</th>
                        <th className="text-center px-3 py-2 text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it, idx) => {
                        const dup = existingSet.has(it.keyword.trim().toLowerCase());
                        return (
                          <tr key={idx} className="border-b border-white/5 last:border-b-0" data-testid={`bulk-preview-row-${idx}`}>
                            <td className="px-3 py-1.5 text-foreground/90">{it.keyword}</td>
                            <td className="px-3 py-1.5 text-muted-foreground">{it.type}</td>
                            <td className="px-3 py-1.5 text-right font-mono tabular-nums text-muted-foreground">{it.search_volume}</td>
                            <td className="px-3 py-1.5 text-muted-foreground">{it.difficulty}</td>
                            <td className="px-3 py-1.5 text-muted-foreground">{it.intent}</td>
                            <td className="px-3 py-1.5 text-center">
                              {dup ? (
                                <Badge variant="outline" className="text-[9px] uppercase bg-amber-500/15 text-amber-300 border-amber-500/30" data-testid={`bulk-preview-dup-${idx}`}>
                                  duplicate
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-[9px] uppercase bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
                                  new
                                </Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Duplicate strategy */}
            {dupeCount > 0 && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 space-y-2" data-testid="bulk-duplicate-strategy">
                <p className="text-[11px] uppercase tracking-[0.08em] font-medium text-amber-400">Duplicate strategy</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <label className={`flex-1 rounded-md border px-3 py-2 cursor-pointer transition-colors ${onDuplicate === 'skip' ? 'border-primary/60 bg-primary/10' : 'border-white/10 hover:border-white/25'}`}>
                    <input
                      type="radio"
                      name="on_duplicate"
                      value="skip"
                      checked={onDuplicate === 'skip'}
                      onChange={() => setOnDuplicate('skip')}
                      className="sr-only"
                      data-testid="bulk-duplicate-skip"
                    />
                    <p className="text-xs font-medium text-foreground">Skip duplicates</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Existing rows are left untouched. Use when the CSV has data you do not want overwriting older values.</p>
                  </label>
                  <label className={`flex-1 rounded-md border px-3 py-2 cursor-pointer transition-colors ${onDuplicate === 'merge' ? 'border-primary/60 bg-primary/10' : 'border-white/10 hover:border-white/25'}`}>
                    <input
                      type="radio"
                      name="on_duplicate"
                      value="merge"
                      checked={onDuplicate === 'merge'}
                      onChange={() => setOnDuplicate('merge')}
                      className="sr-only"
                      data-testid="bulk-duplicate-merge"
                    />
                    <p className="text-xs font-medium text-foreground">Merge duplicates</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Overwrites type / volume / difficulty / intent / notes on the existing row. Its id and week assignment stay the same.</p>
                  </label>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={closeAndReset} disabled={importing}>Cancel</Button>
              <Button
                onClick={submit}
                disabled={importing || items.length === 0 || !weekId}
                className="bg-primary text-primary-foreground gap-1.5"
                data-testid="bulk-import-submit-btn"
              >
                {importing && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Import {items.length > 0 ? `${items.length} row${items.length !== 1 ? 's' : ''}` : ''}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function ResultTile({ label, value, tone, testId }) {
  const toneClass = tone === 'good'
    ? 'border-emerald-500/30 bg-emerald-500/10'
    : tone === 'bad'
      ? 'border-red-500/30 bg-red-500/10'
      : 'border-white/10 bg-background/40';
  const numColor = tone === 'good'
    ? 'text-emerald-300'
    : tone === 'bad'
      ? 'text-red-300'
      : 'text-foreground';
  return (
    <div className={`rounded-md border p-3 ${toneClass}`} data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.08em] font-medium text-muted-foreground">{label}</p>
      <p className={`text-2xl font-medium font-mono tabular-nums leading-none mt-1 ${numColor}`}>{value}</p>
    </div>
  );
}



function ContentDetailModal({ open, card, onClose, onSaved, canEdit, keywords, strategies }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showUploader, setShowUploader] = useState(false);

  useEffect(() => {
    if (open && card) {
      setForm({
        content_title: card.content_title || '',
        content_type: card.content_type || 'Blog Post',
        brief_notes: card.brief_notes || '',
        assigned_to: card.assigned_to || '',
        status: card.status || 'Requested',
        due_date: card.due_date || '',
        published_url: card.published_url || '',
        external_reference_url: card.external_reference_url || '',
        attachments: card.attachments || [],
        keyword_id: card.keyword_id || '',
        monthly_strategy_id: card.monthly_strategy_id || '',
      });
      setShowUploader(false);
    }
  }, [open, card]);

  if (!form) return null;

  const handleUploadComplete = (uploaded) => {
    if (form.attachments.length >= MAX_ATTACHMENTS) {
      toast.error(`Maximum ${MAX_ATTACHMENTS} attachments per record. Remove one to add another.`);
      return;
    }
    setForm(f => ({ ...f, attachments: [...f.attachments, uploaded] }));
    setShowUploader(false);
  };

  const removeAttachment = (idx) => {
    setForm(f => ({ ...f, attachments: f.attachments.filter((_, i) => i !== idx) }));
  };

  const submit = async () => {
    if (!form.content_title.trim()) return toast.error('Content Title is required');
    if (!form.keyword_id) return toast.error('Keyword Target is required');
    if (!form.monthly_strategy_id) return toast.error('Monthly Strategy is required');
    setSaving(true);
    try {
      const r = await api.put(`/content-calendar/${card.id}`, form);
      toast.success('Content saved');
      onSaved(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const attachmentsFull = form.attachments.length >= MAX_ATTACHMENTS;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="content-detail-modal">
        <DialogHeader>
          <DialogTitle>{canEdit ? 'Edit Content' : 'Content Details'}</DialogTitle>
          <DialogDescription>{canEdit ? 'Update the request, upload up to 3 attachments, and move the piece through the pipeline.' : 'Read-only view of this content request. Ask an admin for edit access.'}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FormField label="Content Title" testId="cd-title" className="md:col-span-2">
            <Input value={form.content_title} disabled={!canEdit} onChange={(e) => setForm({ ...form, content_title: e.target.value })} className="bg-background border-white/10" data-testid="cd-title-input" />
          </FormField>
          <FormField label="Content Type" testId="cd-type">
            <Select value={form.content_type} onValueChange={(v) => setForm({ ...form, content_type: v })} disabled={!canEdit}>
              <SelectTrigger className="bg-background border-white/10" data-testid="cd-type-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>{CONTENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </FormField>
          <FormField label="Status" testId="cd-status">
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })} disabled={!canEdit}>
              <SelectTrigger className="bg-background border-white/10" data-testid="cd-status-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS_COLUMNS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </FormField>
          <FormField label="Assigned To" testId="cd-assignee">
            <Input value={form.assigned_to} disabled={!canEdit} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} className="bg-background border-white/10" data-testid="cd-assignee-input" placeholder="Alex, Sheena, ..." />
          </FormField>
          <FormField label="Due Date" testId="cd-due">
            <Input type="date" value={form.due_date} disabled={!canEdit} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="bg-background border-white/10" data-testid="cd-due-input" />
          </FormField>
          <FormField label="Keyword Target" testId="cd-keyword">
            <Select value={form.keyword_id} onValueChange={(v) => setForm({ ...form, keyword_id: v })} disabled={!canEdit}>
              <SelectTrigger className="bg-background border-white/10" data-testid="cd-keyword-trigger"><SelectValue placeholder="Pick a keyword" /></SelectTrigger>
              <SelectContent>
                {keywords.map(k => <SelectItem key={k.id} value={k.id}>{k.keyword}</SelectItem>)}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Monthly Strategy" testId="cd-strategy">
            <Select value={form.monthly_strategy_id} onValueChange={(v) => setForm({ ...form, monthly_strategy_id: v })} disabled={!canEdit}>
              <SelectTrigger className="bg-background border-white/10" data-testid="cd-strategy-trigger"><SelectValue placeholder="Pick a strategy" /></SelectTrigger>
              <SelectContent>
                {strategies.map(s => <SelectItem key={s.id} value={s.id}>{formatDate(s.month, { month: 'short', year: 'numeric' })} - {s.status}</SelectItem>)}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Brief / Notes" testId="cd-brief" className="md:col-span-2">
            <Textarea rows={4} value={form.brief_notes} disabled={!canEdit} onChange={(e) => setForm({ ...form, brief_notes: e.target.value })} className="bg-background border-white/10" data-testid="cd-brief-input" />
          </FormField>
          <FormField label="Published URL" testId="cd-pub-url" className="md:col-span-2">
            <Input value={form.published_url} disabled={!canEdit} onChange={(e) => setForm({ ...form, published_url: e.target.value })} className="bg-background border-white/10" data-testid="cd-pub-url-input" placeholder="https://..." />
            {form.published_url && (
              <a href={form.published_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1" data-testid="cd-pub-url-link">
                <ExternalLink className="h-3 w-3" /> Open link
              </a>
            )}
          </FormField>
          <FormField label="External Reference URL" testId="cd-ext-url" className="md:col-span-2">
            <Input value={form.external_reference_url} disabled={!canEdit} onChange={(e) => setForm({ ...form, external_reference_url: e.target.value })} className="bg-background border-white/10" data-testid="cd-ext-url-input" placeholder="https://competitor.com/example" />
            {form.external_reference_url && (
              <a href={form.external_reference_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1" data-testid="cd-ext-url-link">
                <ExternalLink className="h-3 w-3" /> Open reference
              </a>
            )}
          </FormField>
          <div className="md:col-span-2 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground">
                Attachments <span className="text-muted-foreground/60 normal-case">({form.attachments.length}/{MAX_ATTACHMENTS})</span>
              </Label>
              {canEdit && !attachmentsFull && !showUploader && (
                <Button size="sm" variant="outline" onClick={() => setShowUploader(true)} className="gap-1.5" data-testid="cd-add-attachment-btn">
                  <Plus className="h-3 w-3" /> Add file
                </Button>
              )}
            </div>
            {canEdit && attachmentsFull && !showUploader && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2" data-testid="cd-attachments-limit-error">
                <p className="text-[11px] text-amber-400">Maximum of {MAX_ATTACHMENTS} attachments reached. Remove one to add another.</p>
              </div>
            )}
            <div className="space-y-2">
              {form.attachments.length === 0 && !showUploader && (
                <p className="text-[11px] text-muted-foreground/60 italic">No attachments yet.</p>
              )}
              {form.attachments.map((a, idx) => (
                <div key={a.file_id || idx} className="flex items-center gap-3 rounded-md border border-white/10 bg-background/40 px-3 py-2" data-testid={`cd-attachment-${idx}`}>
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{a.file_name || a.file_id}</p>
                    {a.file_size ? <p className="text-[10px] text-muted-foreground">{(a.file_size / 1024).toFixed(1)} KB</p> : null}
                  </div>
                  <a href={a.file_url || `/api/files/${a.file_id}`} target="_blank" rel="noreferrer" className="text-[11px] text-primary hover:underline">Open</a>
                  {canEdit && (
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => removeAttachment(idx)} data-testid={`cd-remove-attachment-${idx}`}>
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              ))}
              {showUploader && canEdit && (
                <div className="space-y-2">
                  <FileUpload onUploadComplete={handleUploadComplete} />
                  <Button size="sm" variant="ghost" onClick={() => setShowUploader(false)} className="text-xs">Cancel upload</Button>
                </div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Close</Button>
          {canEdit && (
            <Button onClick={submit} disabled={saving} data-testid="cd-save-btn" className="bg-primary text-primary-foreground gap-1.5">
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function FormField({ label, testId, className = '', children }) {
  return (
    <div className={`space-y-1 ${className}`} data-testid={`field-${testId}`}>
      <Label className="text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// PAGE COMPOSITION
// ═══════════════════════════════════════════════════════════════════════════
export default function SEOWorkspacePage() {
  const { hasPerm } = useAuth();
  const canEdit = hasPerm('manage_digital_marketing');

  const [strategies, setStrategies] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [content, setContent] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);

  const [msModalOpen, setMsModalOpen] = useState(false);
  const [msEditing, setMsEditing] = useState(null);
  const [wkModalOpen, setWkModalOpen] = useState(false);
  const [kwModalOpen, setKwModalOpen] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [detailCard, setDetailCard] = useState(null);

  const reloadAll = async () => {
    try {
      const [s, w, k, c, src] = await Promise.all([
        api.get('/monthly-strategies'),
        api.get('/weekly-keywords'),
        api.get('/keywords'),
        api.get('/content-calendar'),
        api.get('/keyword-research-sources'),
      ]);
      setStrategies(s.data || []);
      setWeeks(w.data || []);
      setKeywords(k.data || []);
      setContent(c.data || []);
      setSources(src.data || []);
    } catch (e) {
      console.error('SEO workspace fetch failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reloadAll(); }, []);

  const activeStrategy = useMemo(() => {
    const active = strategies.filter(s => s.status === 'Active');
    return active.find(s => isInCurrentMonth(s.month)) || active[0] || null;
  }, [strategies]);

  const keywordsByWeek = useMemo(() => {
    const m = {};
    for (const kw of keywords) {
      if (!m[kw.week_id]) m[kw.week_id] = [];
      m[kw.week_id].push(kw);
    }
    return m;
  }, [keywords]);

  const handleCardStatusChange = async (cardId, newStatus) => {
    const card = content.find(c => c.id === cardId);
    if (!card) return;
    // Optimistic UI update
    setContent(prev => prev.map(c => (c.id === cardId ? { ...c, status: newStatus } : c)));
    try {
      const payload = { ...card, status: newStatus };
      // Strip fields Mongo tracks internally
      delete payload._id;
      await api.put(`/content-calendar/${cardId}`, payload);
      toast.success(`Moved to "${newStatus}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
      // Revert on failure
      setContent(prev => prev.map(c => (c.id === cardId ? card : c)));
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="seo-workspace-page">
      <div>
        <h1 className="text-[22px] font-medium tracking-tight" data-testid="seo-workspace-title">SEO Workspace</h1>
        <p className="text-xs text-muted-foreground mt-1">Plan monthly focus, group keywords by week, and move content through the pipeline.</p>
      </div>

      {/* Section 1 */}
      <SectionHeader label="Current Monthly Focus" />
      <CurrentMonthlyFocus
        activeStrategy={activeStrategy}
        canEdit={canEdit}
        onCreateClick={() => { setMsEditing(null); setMsModalOpen(true); }}
      />
      <AllStrategiesList
        strategies={strategies}
        canEdit={canEdit}
        onCreateClick={() => { setMsEditing(null); setMsModalOpen(true); }}
        onEditClick={(s) => { setMsEditing(s); setMsModalOpen(true); }}
      />

      {/* Section 2 */}
      <SectionHeader label="Keyword Groups" />
      {loading ? (
        <SkeletonBlock />
      ) : (
        <KeywordGroupsSection
          weeks={weeks}
          keywordsByWeek={keywordsByWeek}
          canEdit={canEdit}
          onAddClick={() => setWkModalOpen(true)}
        />
      )}

      {/* Section 3 */}
      <SectionHeader label="Content Requests" />
      <ContentKanban
        cards={content}
        canEdit={canEdit}
        onCardClick={(c) => setDetailCard(c)}
        onCardStatusChange={handleCardStatusChange}
      />

      {/* Section 4 */}
      <SectionHeader label="Keyword Library" />
      <KeywordLibrary
        keywords={keywords}
        sources={sources}
        weeks={weeks}
        canEdit={canEdit}
        onAddClick={() => setKwModalOpen(true)}
        onImportClick={() => setBulkModalOpen(true)}
      />

      {/* Modals */}
      <MonthlyStrategyModal
        open={msModalOpen}
        onClose={() => { setMsModalOpen(false); setMsEditing(null); }}
        onSaved={() => reloadAll()}
        editing={msEditing}
        weeklyKeywords={weeks}
        strategies={strategies}
      />
      <WeeklyKeywordsModal
        open={wkModalOpen}
        onClose={() => setWkModalOpen(false)}
        onSaved={() => reloadAll()}
        strategies={strategies}
      />
      <KeywordModal
        open={kwModalOpen}
        onClose={() => setKwModalOpen(false)}
        onSaved={() => reloadAll()}
        weeks={weeks}
        sources={sources}
      />
      <BulkKeywordImportModal
        open={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        onSaved={() => reloadAll()}
        weeks={weeks}
        sources={sources}
        existingKeywords={keywords}
      />
      <ContentDetailModal
        open={!!detailCard}
        card={detailCard}
        canEdit={canEdit}
        keywords={keywords}
        strategies={strategies}
        onClose={() => setDetailCard(null)}
        onSaved={() => reloadAll()}
      />
    </div>
  );
}


function SectionHeader({ label }) {
  return (
    <div className="flex items-center gap-2 pt-2">
      <div className="h-px flex-1 bg-white/5" aria-hidden />
      <p className="text-[11px] uppercase tracking-[0.12em] font-medium text-muted-foreground">{label}</p>
      <div className="h-px flex-1 bg-white/5" aria-hidden />
    </div>
  );
}


function SkeletonBlock() {
  return (
    <div className="rounded-md border border-white/10 bg-card/40 p-4">
      <div className="h-4 bg-white/5 rounded w-1/4 animate-pulse mb-3" />
      <div className="h-3 bg-white/5 rounded w-2/3 animate-pulse mb-2" />
      <div className="h-3 bg-white/5 rounded w-1/2 animate-pulse" />
    </div>
  );
}
